import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ClipboardList, Camera, ShieldCheck, AlertTriangle, ArrowRight, Mail } from "lucide-react";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Pick the election and location",
    body: "Open Data Entry, choose the active election, then drill down: Region › Municipal/District › Constituency › Polling Unit. The dropdowns fill in as you go — if a list is empty, ask your admin to set up the hierarchy."
  },
  {
    icon: () => <span className="tabular font-display text-2xl">01</span>,
    title: "Count every candidate carefully",
    body: "Enter the vote count for each candidate exactly as recorded on the official result sheet. Also fill in Registered, Accredited, Valid, and Rejected. The console will warn you live if the candidate votes don't sum to the Valid total."
  },
  {
    icon: Camera,
    title: "Photograph the sheet",
    body: "Take a clear photo of the whole result sheet — good light, no shadows, all four corners visible. If you have a scanner, a PDF is fine too. Then click Attach Sheet and upload it."
  },
  {
    icon: ArrowRight,
    title: "Submit for approval",
    body: "Once the numbers look right, click Submit for Approval. Your submission goes straight to reviewers and admins with a Pending badge — you'll see the status change to Approved (or Rejected with a reason) once they review it."
  },
  {
    icon: ShieldCheck,
    title: "If rejected, resubmit",
    body: "You can resubmit the same polling unit with corrected numbers — the previous version is preserved in the audit history. You cannot resubmit a Pending or already-Approved result; contact your admin if you need to unlock one."
  },
];

const FAQ = [
  {
    q: "What is the ±N warning I see next to a result?",
    a: "It means the sum of candidate votes does not equal the recorded Valid total. It doesn't block submission, but the reviewer will see the same warning. Recheck your Valid line, then submit."
  },
  {
    q: "Who sees my submission?",
    a: "Every reviewer and admin sees it in the Approvals list within seconds. It doesn't appear on the public dashboard until a reviewer approves it (unless the public 'Approved + Pending' toggle is on)."
  },
  {
    q: "The location I need isn't in the list.",
    a: "Ask your admin. Only admins can add or edit polling units. Send them the exact name and code from the official sheet."
  },
  {
    q: "I lost my password.",
    a: "There is no self-service reset in this version — contact your admin. They can rotate your password from the Users page."
  },
];

export default function About() {
  const { user } = useAuth();
  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1100px]">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Field Guide</div>
      <h1 className="font-display text-5xl lg:text-6xl mt-2 leading-[0.95]" data-testid="about-title">How to submit results</h1>
      <p className="text-zinc-400 mt-4 max-w-2xl text-sm lg:text-base">
        A five-step walkthrough for polling agents. Read it once before your first submission — the whole flow takes about two minutes per polling unit once you're used to it.
      </p>

      <div className="mt-12 space-y-6">
        {STEPS.map((s, i) => (
          <div key={i} className="grid grid-cols-[64px_1fr] gap-6 border-l-2 border-emerald-500/40 pl-6 py-2" data-testid={`step-${i+1}`}>
            <div className="pt-1">
              <div className="w-12 h-12 border border-white/20 flex items-center justify-center bg-white/[0.02]">
                {typeof s.icon === "function" && s.icon.name === "" ? <s.icon /> : <s.icon size={20} strokeWidth={1.5} />}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Step {String(i+1).padStart(2, "0")}</div>
              <h2 className="font-display text-2xl lg:text-3xl mt-1 leading-tight">{s.title}</h2>
              <p className="text-zinc-400 mt-2 text-sm lg:text-base leading-relaxed max-w-2xl">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16 border border-amber-500/30 bg-amber-500/[0.06] p-6 flex items-start gap-4" data-testid="hazard-card">
        <AlertTriangle size={24} strokeWidth={1.5} className="text-amber-400 shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-xl">A note on integrity</div>
          <p className="text-sm text-amber-100/80 mt-2 leading-relaxed max-w-2xl">
            Every submission is timestamped and signed with your account. The full history — including any resubmissions — is preserved in the Audit Log. Do not enter tallies you have not personally counted from the original sheet.
          </p>
        </div>
      </div>

      <div className="mt-16">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Common questions</div>
        <h2 className="font-display text-3xl mt-2">FAQ</h2>
        <div className="mt-6 divide-y divide-white/5 border-y border-white/10">
          {FAQ.map((f, i) => (
            <details key={i} className="group py-4" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer flex items-center justify-between text-base lg:text-lg font-medium list-none">
                <span>{f.q}</span>
                <span className="text-zinc-500 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed max-w-2xl">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="mt-16 border border-white/10 bg-white p-6 flex items-start gap-4">
        <Mail size={20} strokeWidth={1.5} className="text-zinc-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Still stuck?</div>
          <p className="text-sm text-zinc-300 mt-2">Contact your operations admin. They can see every one of your submissions and can unlock, correct, or reassign polling units on your behalf.</p>
        </div>
        <Link to="/app/entry" data-testid="go-to-entry" className="whitespace-nowrap px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-white transition-colors">
          Open Data Entry →
        </Link>
      </div>

      <div className="mt-16 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        Signed in as {user?.email} · {user?.role}
      </div>
    </div>
  );
}
